import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getAboutPage } from '../utils/api';
import NotFoundPage from './NotFoundPage';

const team = [
    { name: 'Julian Vane', role: 'CHIEF INNOVATOR', initial: 'J' },
    { name: 'Elena Ross', role: 'HEAD OF ENGINEERING', initial: 'E' },
    { name: 'Marcus Thorne', role: 'LEAD STRATEGIST', initial: 'M' },
    { name: 'Sarah Chen', role: 'HEAD DESIGNER', initial: 'S' },
];

export default function AboutPage() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const safeJsonParse = (str, fallback) => {
        if (!str || typeof str !== 'string') return fallback;
        try {
            return JSON.parse(str);
        } catch (e) {
            console.error("JSON Parse Error:", e);
            return fallback;
        }
    };

    useEffect(() => {
        const loadPage = async () => {
            const page = await getAboutPage();
            if (page) {
                const meta = page.about_meta || {};

                // Helper to strip JSON brackets if the user accidentally entered JSON in a text field
                const cleanText = (txt) => {
                    if (!txt) return "";
                    if (txt.startsWith('[') || txt.startsWith('{')) {
                        try {
                            const parsed = JSON.parse(txt);
                            if (Array.isArray(parsed)) return parsed.map(i => i.value || i.desc || "").join(' ');
                            return txt;
                        } catch (e) { return txt; }
                    }
                    return txt;
                };

                const featuredImage = page._embedded?.['wp:featuredmedia']?.[0]?.source_url;

                setData({
                    title: page.title.rendered || "About Us",
                    featuredImage: featuredImage,
                    desc: page.content.rendered.replace(/<[^>]+>/g, ''),
                    hero_quote: meta.hero_quote || "",
                    hero_years: meta.hero_years || "",
                    mission: cleanText(meta.mission) || "",
                    vision: cleanText(meta.vision) || "",
                    stats: safeJsonParse(meta.stats, []),
                    timeline: safeJsonParse(meta.timeline, []),
                    team: safeJsonParse(meta.team, []),
                    cta_title: meta.cta_title || "",
                    cta_desc: meta.cta_desc || ""
                });
            }
            setLoading(false);
        };
        loadPage();
    }, []);

    if (loading) {
        return (
            <div className="pt-32 px-6 sm:px-8 flex items-center justify-center min-h-screen">
                <div className="w-12 h-12 border-4 border-nocturne-purple border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    if (!data) return <NotFoundPage />;

    return (
        <div className="pt-16">
            {/* Header */}
            <section className="py-20 px-6 sm:px-8 relative overflow-hidden">
                <div className="absolute inset-0 bg-hero-glow pointer-events-none" />
                <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12 items-center">
                    <div className="relative z-10">
                        <p className="section-tag">THE DIGITAL PURPOSE</p>
                        <h1 className="text-5xl md:text-6xl font-black text-nocturne-text leading-tight mb-6 mt-6">
                            {data.title.split(' ')[0]} of the <br />
                            <span className="gradient-text">{data.title.split(' ').slice(1).join(' ')}</span>
                        </h1>
                        <p className="text-nocturne-muted text-lg leading-relaxed mb-8">
                            {data.desc}
                        </p>
                        {data.hero_quote && (
                            <div className="border-l-2 border-nocturne-purple pl-6 py-2 mb-10">
                                <p className="text-nocturne-text italic text-lg opacity-80">
                                    "{data.hero_quote}"
                                </p>
                            </div>
                        )}
                        {data.hero_years && (
                            <div className="flex items-end gap-3">
                                <div className="text-4xl font-black text-nocturne-text">{data.hero_years.split('+')[0]}<span className="text-nocturne-purple">+</span></div>
                                <div className="text-nocturne-muted text-xs font-semibold tracking-widest pb-1.5 uppercase">Years designing</div>
                            </div>
                        )}
                    </div>
                    {/* Hero Image */}
                    <div className="hidden md:block">
                        <div className="rounded-2xl aspect-[4/3] bg-gradient-to-br from-nocturne-cyan/20 to-nocturne-purple/10 border border-nocturne-border flex items-center justify-center overflow-hidden">
                            {data.featuredImage ? (
                                <img src={data.featuredImage} alt={data.title} className="w-full h-full object-cover" />
                            ) : (
                                <div className="text-center">
                                    <div className="text-6xl mb-3">🏙️</div>
                                    <p className="text-nocturne-muted text-sm">Amisubsolutions Studio</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            {/* Mission & Vision */}
            {(data.mission || data.vision) && (
                <section className="py-24 px-6 sm:px-8 bg-nocturne-card border-y border-nocturne-border">
                    <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12">
                        {data.mission && (
                            <div className="card">
                                <div className="text-3xl mb-6">⚡</div>
                                <h3 className="text-2xl font-bold text-nocturne-text mb-4">Our Mission</h3>
                                <p className="text-nocturne-muted leading-relaxed">
                                    {data.mission}
                                </p>
                            </div>
                        )}
                        {data.vision && (
                            <div className="card">
                                <div className="text-3xl mb-6">🔭</div>
                                <h3 className="text-2xl font-bold text-nocturne-text mb-4">Our Vision</h3>
                                <p className="text-nocturne-muted leading-relaxed">
                                    {data.vision}
                                </p>
                            </div>
                        )}
                    </div>
                </section>
            )}

            {/* Stats */}
            {data.stats && data.stats.length > 0 && (
                <section className="py-24 px-6 sm:px-8">
                    <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-3 gap-8 text-center">
                        {data.stats.map((s, i) => (
                            <div key={i}>
                                <div className="text-5xl font-black text-nocturne-text mb-2">{s.value}</div>
                                <div className="text-nocturne-muted text-sm font-semibold tracking-widest uppercase">{s.label}</div>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {/* Timeline */}
            {data.timeline && data.timeline.length > 0 && (
                <section className="py-24 px-6 sm:px-8 bg-nocturne-card border-y border-nocturne-border overflow-hidden">
                    <div className="max-w-7xl mx-auto">
                        <h2 className="text-3xl font-bold text-nocturne-text mb-16 text-center">Our Journey</h2>
                        <div className="relative">
                            <div className="absolute left-1/2 top-0 bottom-0 w-px bg-nocturne-border -translate-x-1/2 hidden md:block" />
                            <div className="space-y-12">
                                {data.timeline.map((item, i) => (
                                    <div key={i} className={`flex flex-col md:flex-row items-center gap-8 ${i % 2 !== 0 ? 'md:flex-row-reverse' : ''}`}>
                                        <div className="flex-1 text-center md:text-right">
                                            {i % 2 === 0 && (
                                                <>
                                                    <div className="text-nocturne-purple font-bold text-xl mb-1">{item.year}</div>
                                                    <p className="text-nocturne-muted text-sm">{item.desc}</p>
                                                </>
                                            )}
                                        </div>
                                        <div className="w-4 h-4 rounded-full bg-nocturne-purple border-4 border-nocturne-bg z-10 shrink-0" />
                                        <div className="flex-1 text-center md:text-left">
                                            {i % 2 !== 0 && (
                                                <>
                                                    <div className="text-nocturne-purple font-bold text-xl mb-1">{item.year}</div>
                                                    <p className="text-nocturne-muted text-sm">{item.desc}</p>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* Team */}
            {data.team && data.team.length > 0 && (
                <section className="py-24 px-6 sm:px-8">
                    <div className="max-w-7xl mx-auto">
                        <div className="text-center mb-16">
                            <p className="section-tag inline-block">THE EXPERTS</p>
                            <h2 className="text-4xl font-bold text-nocturne-text mt-4">Meet the <span className="gradient-text">Architects</span></h2>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                            {data.team.map((member, i) => (
                                <div key={i} className="card text-center group">
                                    <div className="w-20 h-20 rounded-full bg-nocturne-purple/20 border border-nocturne-purple/40 mx-auto mb-6 flex items-center justify-center text-3xl group-hover:bg-nocturne-purple/40 transition-all">
                                        👤
                                    </div>
                                    <h3 className="text-nocturne-text font-bold mb-1">{member.name}</h3>
                                    <div className="text-nocturne-muted text-xs font-semibold tracking-wider uppercase">{member.role}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* CTA */}
            {(data.cta_title || data.cta_desc) && (
                <section className="py-24 px-6 sm:px-8">
                    <div className="max-w-3xl mx-auto text-center">
                        {data.cta_title && (
                            <h2 className="text-4xl md:text-5xl font-black text-nocturne-text mb-4">
                                {data.cta_title.split(' ').slice(0, -1).join(' ')} <span className="gradient-text">{data.cta_title.split(' ').pop()}</span>
                            </h2>
                        )}
                        {data.cta_desc && <p className="text-nocturne-muted mb-8">{data.cta_desc}</p>}
                        <Link to="/contact" className="btn-primary inline-block px-10 py-4 text-base">
                            Initiate Project
                        </Link>
                    </div>
                </section>
            )}
        </div>
    );
}
